import React, { useState, useCallback } from 'react';
import { PaymentFormData, TransferDetail, ModalBadgeProps } from '../../types/modal.types';
import { useLanguage } from '../../context/LanguageContext';
import ModalButton from '../UI/ModalButton';
import Toast from '../UI/Toast';

interface PaymentFormProps {
  initialData?: Partial<PaymentFormData>;
  onSubmit: (ref?: string) => void;
  confirming?: boolean;
  error?: string | null;
  paymentStatus?: string;
  timeExpired?: boolean;
  badge?: ModalBadgeProps;
  requiresConfirmRef?: boolean;
}

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/[^\d+]/g, '');

  let localNumber = '';

  if (cleaned.startsWith('+58')) {
    localNumber = '0' + cleaned.slice(3);
  } else if (cleaned.startsWith('58')) {
    localNumber = '0' + cleaned.slice(2);
  } else if (cleaned.startsWith('0')) {
    localNumber = cleaned;
  } else {
    localNumber = '0' + cleaned;
  }

  if (localNumber.length >= 11) {
    return `${localNumber.slice(0, 4)}-${localNumber.slice(4)}`;
  }

  return phone;
};

const PaymentForm: React.FC<PaymentFormProps> = ({
  initialData = {},
  onSubmit,
  confirming = false,
  error,
  paymentStatus,
  timeExpired = false,
  badge,
  requiresConfirmRef = false
}) => {
  const { t } = useLanguage();
  const [showToast, setShowToast] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [refValue, setRefValue] = useState('');
  const [accordionOpen, setAccordionOpen] = useState(false);

  const formattedPhone = initialData.phoneNumber ? formatPhoneNumber(initialData.phoneNumber) : '';

  const transferDetails: TransferDetail[] = [
    { label: t('phoneNumber'), value: formattedPhone },
    { label: t('accountHolder'), value: initialData.accountHolder || '' },
    { label: t('bankName'), value: initialData.bankName || '' },
    { label: t('idNumber'), value: initialData.idNumber || '' },
  ].filter(detail => detail.value);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirming && !timeExpired) {
      onSubmit(requiresConfirmRef ? refValue : undefined);
    }
  };

  const handleRefChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setRefValue(value);
  };

  const getRefPreview = () => {
    if (refValue.length >= 4) {
      const lastFour = refValue.slice(-4);
      const masked = '*'.repeat(Math.max(0, refValue.length - 4));
      return `${masked}${lastFour}`;
    }
    return '';
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleToastClose = useCallback(() => {
    setShowToast(false);
  }, []);

  const getCopyIcon = () => {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="9" y="9" width="13" height="13" rx="2" stroke="#9CA3AF" strokeWidth="2"/>
        <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#9CA3AF" strokeWidth="2"/>
      </svg>
    );
  };

  const getButtonText = () => {
    if (confirming) return t('processing');
    if (timeExpired) return t('timeExpired');
    if (paymentStatus === 'confirmed') return t('paymentConfirmed');
    return t('sentMoney');
  };

  const isButtonDisabled = confirming || timeExpired || paymentStatus === 'confirmed' || (requiresConfirmRef && refValue.length < 4);

  return (
    <>
      <Toast
        message={t('successfullyCopied')}
        visible={showToast}
        onClose={handleToastClose}
      />

      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="transfer-details">
          <h3 className="transfer-details__title">{t('copyTransferDetails')}</h3>

          <div className="transfer-details__list">
            {transferDetails.map((detail, index) => (
              <div key={index} className="transfer-detail">
                <div className="transfer-detail__content">
                  <span className="transfer-detail__label">{detail.label}</span>
                  <span className="transfer-detail__value">{detail.value}</span>
                </div>
                <div className="transfer-detail__copy-wrapper">
                  <button
                    type="button"
                    className="transfer-detail__copy"
                    onClick={() => handleCopy(detail.value)}
                    onMouseEnter={() => setHoveredButton(detail.label)}
                    onMouseLeave={() => setHoveredButton(null)}
                  >
                    {getCopyIcon()}
                  </button>
                  {hoveredButton === detail.label && (
                    <div className="transfer-detail__tooltip">
                      {t('copy')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {requiresConfirmRef && (
          <div className="reference-section">
            <div className="reference-section__hint">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#3B82F6" strokeWidth="2"/>
                <path d="M12 16V12M12 8H12.01" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{t('referenceInputHint')}</span>
            </div>

            <div className="reference-section__input-wrapper">
              <label className="reference-section__label">{t('referenceInputLabel')}</label>
              <input
                type="text"
                className="reference-section__input"
                value={refValue}
                onChange={handleRefChange}
                placeholder={t('referenceInputPlaceholder')}
                maxLength={20}
              />
              {refValue.length >= 4 && (
                <div className="reference-section__preview">
                  {getRefPreview()}
                </div>
              )}
            </div>

            <div className="reference-section__accordion">
              <button
                type="button"
                className={`reference-section__accordion-header ${accordionOpen ? 'reference-section__accordion-header--open' : ''}`}
                onClick={() => setAccordionOpen(!accordionOpen)}
              >
                <span>{t('referenceAccordionTitle')}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`reference-section__accordion-icon ${accordionOpen ? 'reference-section__accordion-icon--open' : ''}`}
                >
                  <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {accordionOpen && (
                <div className="reference-section__accordion-content">
                  <ul>
                    <li>{t('referenceAccordionItem1')}</li>
                    <li>{t('referenceAccordionItem2')}</li>
                    <li>{t('referenceAccordionItem3')}</li>
                    <li>{t('referenceAccordionItem4')}</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="payment-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {badge && (
          <div className="payment-form__status">
            <div className="payment-form__status-left">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#111827" strokeWidth="2"/>
                <path d="M12 6V12L16 14" stroke="#111827" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{t('waitingForTransfer')}</span>
            </div>
            <div className={`payment-form__timer ${badge.variant === 'danger' ? 'payment-form__timer--danger' : ''}`}>
              {badge.time}
            </div>
          </div>
        )}

        <div className="payment-form__actions">
          <ModalButton
            variant="primary"
            type="submit"
            disabled={isButtonDisabled}
          >
            {getButtonText()}
          </ModalButton>

          <p className="payment-form__hint">
            {t('sendExactAmount')}
          </p>
        </div>
      </form>
    </>
  );
};

export default PaymentForm;
